import * as Yup from 'yup';
import {FormikValues} from 'formik';
import LabelLanguageManager from './LabelLanguageManager';
import {LanguageId} from '../../../models/utils/Languages';
import {SectionId} from '../../../models/form/types';
import FieldConfiguration, {TranslatedWord} from '../../../models/form/FieldConfiguration';
import {FieldConfigurationQL} from '../../../models/graphql/FieldConfigurationQL';
import {handleError, handleSuccess} from '../../../utils/Apollo';
import {FetchResult} from '@apollo/client';
import {ActionType, FormTypeContextData} from '../types';

export default class SimpleFormCompanion {
  readonly labelManager: LabelLanguageManager

  constructor(
    protected readonly sectionId: SectionId | undefined,
    protected readonly translations: LanguageId[],
    protected readonly formTypeContext: FormTypeContextData,
    readonly configuration?: FieldConfiguration,
  ) {
    this.labelManager = new LabelLanguageManager(this.translations, this.configuration);
  }

  handleFieldSubmitRequest(
    submit: Promise<FetchResult<{ field: FieldConfigurationQL }>>,
    onCreateUpdate: (action: ActionType, el?: FieldConfiguration) => void,
  ) {
    return submit.then(
      (result) => {
        const msg = this.configuration ? `${this.formTypeContext.capitalizeToken()} updated` : `${this.formTypeContext.capitalizeToken()} added`
        handleSuccess(msg);
        if (result.data?.field) {
          const newConfiguration = new FieldConfiguration(result.data?.field);
          onCreateUpdate(this.configuration ? ActionType.Updated : ActionType.Created, newConfiguration);
        }
      },
      (apolloError) => handleError(apolloError),
    );
  }

  prepareInput(values: FormikValues) {
    const labels: TranslatedWord[] = [];
    Object.entries(values).forEach((value) => {
      const [langId, label] = value;
      if (this.translations.indexOf(langId) >= 0) {
        labels.push({language: langId, translation: label});
      }
    });
    if (this.configuration) {
      return {
        data: {labels, label: values.label, sectionId: this.sectionId},
        type: 'EvaluationFormType',
        id: this.configuration.id,
      };
    }
    return {
      data: {labels, label: values.label, sectionId: this.sectionId}, type: 'EvaluationFormType',
    };
  }

  getInitialFormValues(): Record<string, unknown> {
    return Object.assign({label: this.configuration?.field.label || ''}, this.labelManager.initialFormValues());
  }

  getValidationObject() {
    const schema = Object.assign(
      {label: Yup.string().min(1, 'Must not be empty').required('Required')},
      this.labelManager.validationObject(),
    );
    return Yup.object(schema);
  }
}
